<template>
  <div class="superadmin-stats">

    <!-- ══════════════════════════════════════════════
         PAGE HEADER
    ══════════════════════════════════════════════ -->
    <div class="config-panel-header sa-header-flex">
      <div>
        <h2 class="text-h5 font-weight-bold" :style="{ color: $getColor('vuetifyColor1') }">
          <v-icon :color="$getColor('vuetifyColor1')" large class="mr-2">mdi-chart-box-outline</v-icon>
          Panel de Estadísticas Global
        </h2>
      </div>

      <div class="sa-header-actions">
        <v-select v-model="selectedYear" :items="yearOptions" label="Año" outlined dense hide-details
          class="sa-year-picker" @change="loadAllData" prepend-inner-icon="mdi-calendar" />
        <v-btn icon :loading="anyLoading" @click="loadAllData" class="sa-refresh-btn" title="Refrescar datos">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <v-divider class="mb-6"></v-divider>

    <!-- ══════════════════════════════════════════════
         KPI CARDS ROW
    ══════════════════════════════════════════════ -->
    <v-row class="sa-kpi-row" no-gutters>
      <v-col v-for="kpi in kpiCards" :key="kpi.key" cols="12" sm="6" md="4" lg="2" class="px-1">
        <div class="sa-kpi-card">
          <div class="sa-kpi-card__icon-wrap">
            <v-icon :color="kpi.iconColor" size="28">{{ kpi.icon }}</v-icon>
          </div>
          <div class="sa-kpi-card__body">
            <div class="sa-kpi-card__value">
              <span v-if="loadingSummary" class="sa-skeleton sa-skeleton--sm"></span>
              <span v-else>{{ formatKpiValue(kpi) }}</span>
            </div>
            <div class="sa-kpi-card__label">{{ kpi.label }}</div>
          </div>
          <div class="sa-kpi-card__glow"></div>
        </div>
      </v-col>
    </v-row>

    <!-- ══════════════════════════════════════════════
         TAB NAVIGATION
    ══════════════════════════════════════════════ -->
    <div class="mdtec-card no-padding dashboard_box mb-0">
      <v-tabs v-model="activeTab" centered grow background-color="transparent" :color="$getColor('vuetifyColor1')"
        class="sa-tabs" show-arrows>
        <v-tabs-slider></v-tabs-slider>
        <v-tab v-for="tab in tabs" :key="tab.key">
          <v-icon left small>{{ tab.icon }}</v-icon>
          {{ tab.label }}
        </v-tab>
      </v-tabs>
    </div>

    <!-- ══════════════════════════════════════════════
         TAB CONTENT
    ══════════════════════════════════════════════ -->
    <div class="mdtec-card sa-content-card">

      <!-- ─── TAB 0: FICHAJES ─── -->
      <div v-if="activeTab === 0" class="sa-tab-pane">
        <div class="sa-tab-content">
          <v-row>
            <!-- CHART: fichajes por centro -->
            <v-col cols="12" lg="8">
              <div class="sa-panel">
                <div class="sa-panel__header">
                  <v-icon class="mr-2" color="primary">mdi-chart-bar</v-icon>
                  <span>Fichajes por Centro de Trabajo — {{ selectedYear }}</span>
                </div>
                <div class="sa-panel__body sa-chart-wrap">
                  <div v-if="loadingClock" class="sa-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="52" />
                  </div>
                  <SuperAdminBarChart canvas-id="chart-clock-wc" :labels="clockByWcLabels" :datasets="clockByWcDatasets"
                    y-label="Fichajes" />
                </div>
              </div>
            </v-col>

            <!-- CHART: manuales vs administrador -->
            <v-col cols="12" lg="4">
              <div class="sa-panel">
                <div class="sa-panel__header">
                  <v-icon class="mr-2" color="warning">mdi-account-edit</v-icon>
                  <span>Fichajes Especiales — {{ selectedYear }}</span>
                </div>
                <div class="sa-panel__body" style="height:280px; overflow-y:auto;">
                  <div v-if="loadingManual" class="sa-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="40" />
                  </div>
                  <div v-else-if="manualData.length === 0" class="sa-empty">
                    <v-icon size="48" class="text--disabled">mdi-gesture-tap-hold</v-icon>
                    <p>Sin datos especiales en {{ selectedYear }}</p>
                  </div>
                  <v-simple-table v-else dense class="sa-mini-table">
                    <thead>
                      <tr>
                        <th>Centro</th>
                        <th class="text-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }"><span v-on="on">Manual</span></template>
                            <span>Fichajes manuales del trabajador</span>
                          </v-tooltip>
                        </th>
                        <th class="text-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }"><span v-on="on">Admin</span></template>
                            <span>Fichajes introducidos por administrador</span>
                          </v-tooltip>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in manualData" :key="row.workCenterId">
                        <td class="text-truncate" style="max-width:140px">{{ row.workCenterName }}</td>
                        <td class="text-center">
                          <v-chip x-small :color="row.manual > 0 ? 'warning' : 'default'" dark>{{ row.manual }}</v-chip>
                        </td>
                        <td class="text-center">
                          <v-chip x-small :color="row.admin > 0 ? 'error' : 'default'" dark>{{ row.admin }}</v-chip>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- CHART: fichajes mensuales evolución -->
          <div class="sa-panel mt-4">
            <div class="sa-panel__header">
              <v-icon class="mr-2" color="primary">mdi-chart-line</v-icon>
              <span>Evolución Mensual de Fichajes — {{ selectedYear }}</span>
            </div>
            <div class="sa-panel__body sa-chart-wrap" style="height:320px">
              <div v-if="loadingMonthly" class="sa-loading-overlay">
                <v-progress-circular indeterminate color="primary" size="52" />
              </div>
              <SuperAdminBarChart canvas-id="chart-monthly-clock" :labels="monthLabels" :datasets="monthlyClockDatasets"
                y-label="Fichajes" />
            </div>
          </div>

        </div>
      </div>

      <!-- ─── TAB 1: PERSONAL ─── -->
      <div v-if="activeTab === 1" class="sa-tab-pane">
        <div class="sa-tab-content">
          <v-row>
            <!-- CHART: trabajadores por centro -->
            <v-col cols="12" md="7">
              <div class="sa-panel">
                <div class="sa-panel__header">
                  <v-icon class="mr-2" color="primary">mdi-account-group</v-icon>
                  <span>Distribución de Personal por Centro</span>
                </div>
                <div class="sa-panel__body sa-chart-wrap" style="height:340px">
                  <div v-if="loadingWorkers" class="sa-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="52" />
                  </div>
                  <SuperAdminBarChart canvas-id="chart-workers-wc" :labels="workersLabels" :datasets="workersDatasets"
                    :horizontal="true" y-label="Trabajadores" />
                </div>
              </div>
            </v-col>

            <!-- TABLE: ranking -->
            <v-col cols="12" md="5">
              <div class="sa-panel sa-ranking-panel">
                <div class="sa-panel__header">
                  <v-icon class="mr-2" color="success">mdi-trophy</v-icon>
                  <span>Ranking de Centros</span>
                </div>
                <div class="sa-panel__body" style="overflow-y:auto; max-height:340px">
                  <div v-if="loadingWorkers" class="sa-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="40" />
                  </div>
                  <div v-else-if="workersData.length === 0" class="sa-empty">
                    <v-icon size="48" class="text--disabled">mdi-account-off</v-icon>
                    <p>Sin datos de personal disponibles</p>
                  </div>
                  <div v-else>
                    <div v-for="(row, idx) in workersSorted" :key="row.workCenterId" class="sa-ranking-item"
                      :class="{ 'sa-ranking-item--top': idx < 3 }">
                      <div class="sa-ranking-item__pos">
                        <v-icon v-if="idx === 0" color="amber darken-1" small>mdi-medal</v-icon>
                        <v-icon v-else-if="idx === 1" color="grey lighten-1" small>mdi-medal</v-icon>
                        <v-icon v-else-if="idx === 2" color="brown lighten-2" small>mdi-medal</v-icon>
                        <span v-else class="rank-num">{{ idx + 1 }}</span>
                      </div>
                      <div class="sa-ranking-item__name">{{ row.workCenterName }}</div>
                      <div class="sa-ranking-item__bar-wrap">
                        <div class="sa-ranking-item__bar" :style="{ width: workerBarWidth(row.workers) + '%' }"></div>
                      </div>
                      <div class="sa-ranking-item__count">{{ row.workers }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- ─── TAB 2: INCIDENCIAS ─── -->
      <div v-if="activeTab === 2" class="sa-tab-pane">
        <div class="sa-tab-content">
          <v-row>
            <!-- CHART: incidencias por centro -->
            <v-col cols="12" md="8">
              <div class="sa-panel">
                <div class="sa-panel__header">
                  <v-icon class="mr-2" color="warning">mdi-alert-circle</v-icon>
                  <span>Incidencias por Centro de Trabajo</span>
                  <v-spacer />
                  <div class="sa-legend-inline">
                    <span class="sa-legend-dot sa-legend-dot--error"></span><span>Sin resolver</span>
                    <span class="sa-legend-dot sa-legend-dot--success"></span><span>Resueltas</span>
                  </div>
                </div>
                <div class="sa-panel__body sa-chart-wrap" style="height:340px">
                  <div v-if="loadingIncidences" class="sa-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="52" />
                  </div>
                  <SuperAdminBarChart canvas-id="chart-incidences-wc" :labels="incidencesLabels"
                    :datasets="incidencesDatasets" y-label="Incidencias" />
                </div>
              </div>
            </v-col>

            <!-- KPIs incidencias -->
            <v-col cols="12" md="4">
              <v-row class="ma-0">
                <v-col cols="12" class="pa-2">
                  <div class="sa-stat-mini sa-stat-mini--danger">
                    <v-icon size="32" color="error">mdi-alert</v-icon>
                    <div>
                      <div class="sa-stat-mini__value">{{ totalUnsolvedIncidences }}</div>
                      <div class="sa-stat-mini__label">Incidencias sin resolver</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <div class="sa-stat-mini sa-stat-mini--success">
                    <v-icon size="32" color="success">mdi-check-circle</v-icon>
                    <div>
                      <div class="sa-stat-mini__value">{{ totalSolvedIncidences }}</div>
                      <div class="sa-stat-mini__label">Incidencias resueltas</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <div class="sa-stat-mini">
                    <v-icon size="32" color="info">mdi-percent</v-icon>
                    <div>
                      <div class="sa-stat-mini__value">{{ resolutionRate }}%</div>
                      <div class="sa-stat-mini__label">Tasa de resolución</div>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- Top centros con más incidencias sin resolver -->
              <div class="sa-panel mt-0">
                <div class="sa-panel__header" style="font-size:0.8rem">
                  <v-icon small color="error" class="mr-1">mdi-fire</v-icon>
                  Centros más críticos
                </div>
                <div class="sa-panel__body">
                  <div v-for="row in incidencesTopUnsolved" :key="row.workCenterId" class="sa-critical-row">
                    <span class="sa-critical-row__name">{{ row.workCenterName }}</span>
                    <v-chip x-small color="error" dark>{{ row.unsolved }}</v-chip>
                  </div>
                  <div v-if="incidencesTopUnsolved.length === 0" class="sa-empty pa-4">
                    <v-icon color="success">mdi-check-all</v-icon>
                    <p class="mb-0">Sin incidencias críticas</p>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- ─── TAB 3: N3 ANTICIPOS ─── -->
      <div v-if="activeTab === 3" class="sa-tab-pane">
        <div class="sa-tab-content sa-n3-content" v-if="availableN3">
          <v-row>
            <!-- CHART: importe N3 por centro -->
            <v-col cols="12" md="8">
              <div class="sa-panel">
                <div class="sa-panel__header">
                  <v-icon class="mr-2" color="success">mdi-cash-multiple</v-icon>
                  <span>Anticipos N3 por Centro — {{ selectedYear }}</span>
                </div>
                <div class="sa-panel__body sa-chart-wrap" style="height:320px">
                  <div v-if="loadingN3" class="sa-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="52" />
                  </div>
                  <SuperAdminBarChart canvas-id="chart-n3-wc" :labels="n3Labels" :datasets="n3Datasets"
                    y-label="Importe (€)" />
                </div>
              </div>
            </v-col>

            <!-- KPIs N3 -->
            <v-col cols="12" md="4">
              <v-row class="ma-0">
                <v-col cols="12" class="pa-2">
                  <div class="sa-stat-mini sa-stat-mini--gold">
                    <v-icon size="32" color="amber darken-2">mdi-currency-eur</v-icon>
                    <div>
                      <div class="sa-stat-mini__value">{{ totalN3Amount.toFixed(2) }} €</div>
                      <div class="sa-stat-mini__label">Importe total N3 ({{ selectedYear }})</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <div class="sa-stat-mini">
                    <v-icon size="32" color="info">mdi-file-document-multiple</v-icon>
                    <div>
                      <div class="sa-stat-mini__value">{{ totalN3Count }}</div>
                      <div class="sa-stat-mini__label">Total solicitudes N3</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" class="pa-2" v-if="totalN3Count > 0">
                  <div class="sa-stat-mini">
                    <v-icon size="32" color="primary">mdi-calculator</v-icon>
                    <div>
                      <div class="sa-stat-mini__value">{{ (totalN3Amount / totalN3Count).toFixed(2) }} €</div>
                      <div class="sa-stat-mini__label">Promedio por solicitud</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- CHART: N3 mensual -->
          <div class="sa-panel mt-4">
            <div class="sa-panel__header">
              <v-icon class="mr-2" color="success">mdi-chart-areaspline</v-icon>
              <span>Evolución Mensual de N3 — {{ selectedYear }}</span>
            </div>
            <div class="sa-panel__body sa-chart-wrap" style="height:300px">
              <div v-if="loadingN3Monthly" class="sa-loading-overlay">
                <v-progress-circular indeterminate color="primary" size="52" />
              </div>
              <SuperAdminBarChart canvas-id="chart-n3-monthly" :labels="monthLabels" :datasets="n3MonthlyDatasets"
                y-label="Importe (€)" />
            </div>
          </div>

        </div>
        <div v-else class="sa-tab-content sa-empty">
          <v-icon size="64" class="text--disabled">mdi-cash-remove</v-icon>
          <p class="text--secondary">El módulo N3/Anticipos no está habilitado en este sistema.</p>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import superAdminStatsService from '@/services/superadmin-stats.service';
import SuperAdminBarChart from '@/components/Charts/SuperAdminBarChart.vue';

const MONTH_NAMES = [
  'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
  'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC',
];

export default {
  name: 'SuperAdminStats',
  components: { SuperAdminBarChart },

  data() {
    const currentYear = new Date().getFullYear();

    return {
      activeTab: 0,
      selectedYear: currentYear,
      yearOptions: [currentYear - 2, currentYear - 1, currentYear, currentYear + 1],

      // loading flags
      loadingSummary: false,
      loadingWorkers: false,
      loadingClock: false,
      loadingManual: false,
      loadingIncidences: false,
      loadingN3: false,
      loadingN3Monthly: false,
      loadingMonthly: false,

      // data
      summary: null,
      workersData: [],
      clockData: [],
      manualData: [],
      incidencesData: [],
      n3Data: [],
      n3MonthlyData: [],
      monthlyClockData: [],

      tabs: [
        { key: 'fichajes', label: 'Fichajes', icon: 'mdi-clock-check-outline' },
        { key: 'personal', label: 'Personal', icon: 'mdi-account-group' },
        { key: 'incidencias', label: 'Incidencias', icon: 'mdi-alert-circle-outline' },
        { key: 'n3', label: 'N3 / Anticipos', icon: 'mdi-cash-multiple' },
      ],
    };
  },

  computed: {
    availableN3() {
      return this.$store.state.availableN3 === 'true' || this.$store.state.availableN3 === true;
    },
    anyLoading() {
      return this.loadingSummary || this.loadingWorkers || this.loadingClock
        || this.loadingManual || this.loadingIncidences || this.loadingN3
        || this.loadingN3Monthly || this.loadingMonthly;
    },
    now() {
      return new Date().toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' });
    },

    // ── KPI CARDS ──
    kpiCards() {
      const s = this.summary || {};
      return [
        {
          key: 'centers',
          label: 'Centros de Trabajo',
          icon: 'mdi-office-building-marker',
          iconColor: 'primary',
          value: s.totalWorkCenters,
          cls: 'sa-kpi-card--primary',
        },
        {
          key: 'workers',
          label: 'Trabajadores',
          icon: 'mdi-account-group',
          iconColor: 'info',
          value: s.totalWorkers,
          cls: 'sa-kpi-card--info',
        },
        {
          key: 'clock',
          label: 'Fichajes Totales',
          icon: 'mdi-clock-check',
          iconColor: 'success',
          value: s.totalClockEntries,
          cls: 'sa-kpi-card--success',
        },
        {
          key: 'unsolved',
          label: 'Incidencias Sin Resolver',
          icon: 'mdi-alert',
          iconColor: 'error',
          value: s.totalUnsolvedIncidences,
          cls: 'sa-kpi-card--danger',
        },
        {
          key: 'n3',
          label: 'Importe N3 Año',
          icon: 'mdi-currency-eur',
          iconColor: 'amber darken-2',
          value: s.totalN3AmountCurrentYear,
          isCurrency: true,
          cls: 'sa-kpi-card--gold',
        },
        {
          key: 'year',
          label: 'Año de Referencia',
          icon: 'mdi-calendar',
          iconColor: 'secondary',
          value: s.year,
          cls: 'sa-kpi-card--secondary',
        },
      ];
    },

    // ── WORKERS tab ──
    workersLabels() {
      return this.workersData.map((r) => this.shortName(r.workCenterName));
    },
    workersDatasets() {
      return [{
        label: 'Trabajadores',
        data: this.workersData.map((r) => r.workers),
      }];
    },
    workersSorted() {
      return [...this.workersData].sort((a, b) => b.workers - a.workers);
    },

    // ── CLOCK tab ──
    clockByWcLabels() {
      return this.clockData.map((r) => this.shortName(r.workCenterName));
    },
    clockByWcDatasets() {
      return [{
        label: 'Fichajes',
        data: this.clockData.map((r) => r.entries),
      }];
    },

    monthlyClockDatasets() {
      // Build a matrix: each work center = one dataset
      const centers = [...new Set(this.monthlyClockData.map((r) => r.workCenterName))];
      if (centers.length === 0) return [{ label: 'Sin datos', data: Array(12).fill(0) }];

      return centers.map((name) => {
        const monthlyValues = Array(12).fill(0);
        this.monthlyClockData
          .filter((r) => r.workCenterName === name)
          .forEach((r) => { monthlyValues[r.month - 1] = r.count; });
        return { label: this.shortName(name), data: monthlyValues };
      });
    },

    // ── INCIDENCES tab ──
    incidencesLabels() {
      return this.incidencesData.map((r) => this.shortName(r.workCenterName));
    },
    incidencesDatasets() {
      return [
        {
          label: 'Sin resolver',
          data: this.incidencesData.map((r) => r.unsolved),
          backgroundColor: '#EF5350',
        },
        {
          label: 'Resueltas',
          data: this.incidencesData.map((r) => r.solved),
          backgroundColor: '#66BB6A',
        },
      ];
    },
    totalUnsolvedIncidences() {
      return this.incidencesData.reduce((acc, r) => acc + (r.unsolved || 0), 0);
    },
    totalSolvedIncidences() {
      return this.incidencesData.reduce((acc, r) => acc + (r.solved || 0), 0);
    },
    resolutionRate() {
      const total = this.totalUnsolvedIncidences + this.totalSolvedIncidences;
      if (total === 0) return '100';
      return ((this.totalSolvedIncidences / total) * 100).toFixed(1);
    },
    incidencesTopUnsolved() {
      return [...this.incidencesData]
        .filter((r) => r.unsolved > 0)
        .sort((a, b) => b.unsolved - a.unsolved)
        .slice(0, 5);
    },

    // ── N3 tab ──
    n3Labels() {
      return this.n3Data.map((r) => this.shortName(r.workCenterName));
    },
    n3Datasets() {
      return [{
        label: 'Importe N3 (€)',
        data: this.n3Data.map((r) => parseFloat(r.amount) || 0),
        backgroundColor: '#FFC107',
      }];
    },
    totalN3Amount() {
      return this.n3Data.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
    },
    totalN3Count() {
      return this.n3Data.reduce((acc, r) => acc + (r.count || 0), 0);
    },
    n3MonthlyDatasets() {
      const centers = [...new Set(this.n3MonthlyData.map((r) => r.workCenterName))];
      if (centers.length === 0) return [{ label: 'Sin datos', data: Array(12).fill(0) }];
      return centers.map((name) => {
        const monthlyValues = Array(12).fill(0);
        this.n3MonthlyData
          .filter((r) => r.workCenterName === name)
          .forEach((r) => { monthlyValues[r.month - 1] = parseFloat(r.amount) || 0; });
        return { label: this.shortName(name), data: monthlyValues };
      });
    },

    monthLabels() { return MONTH_NAMES; },
  },

  mounted() {
    this.loadAllData();
  },

  methods: {
    async loadAllData() {
      await Promise.all([
        this.loadSummary(),
        this.loadWorkers(),
        this.loadClockEntries(),
        this.loadIncidences(),
        this.loadN3(),
        this.loadN3Monthly(),
        this.loadMonthlyClockEntries(),
      ]);
    },

    async loadSummary() {
      this.loadingSummary = true;
      try {
        this.summary = await superAdminStatsService.getSummary();
      } catch (e) { console.error('summary:', e); } finally { this.loadingSummary = false; }
    },
    async loadWorkers() {
      this.loadingWorkers = true;
      try {
        this.workersData = await superAdminStatsService.getWorkersByWorkCenter();
      } catch (e) { console.error('workers:', e); } finally { this.loadingWorkers = false; }
    },
    async loadClockEntries() {
      this.loadingClock = true;
      this.loadingManual = true;
      try {
        const ds = `${this.selectedYear}-01-01T00:00:00`;
        const de = `${this.selectedYear}-12-31T23:59:59`;
        [this.clockData, this.manualData] = await Promise.all([
          superAdminStatsService.getClockEntriesByWorkCenter(ds, de),
          superAdminStatsService.getManualClockEntriesByWorkCenter(ds, de),
        ]);
      } catch (e) {
        console.error('clock:', e);
      } finally {
        this.loadingClock = false;
        this.loadingManual = false;
      }
    },
    async loadIncidences() {
      this.loadingIncidences = true;
      try {
        this.incidencesData = await superAdminStatsService.getIncidencesByWorkCenter();
      } catch (e) { console.error('incidences:', e); } finally { this.loadingIncidences = false; }
    },
    async loadN3() {
      this.loadingN3 = true;
      try {
        this.n3Data = await superAdminStatsService.getN3ByWorkCenter(this.selectedYear);
      } catch (e) { console.error('n3:', e); } finally { this.loadingN3 = false; }
    },
    async loadN3Monthly() {
      this.loadingN3Monthly = true;
      try {
        this.n3MonthlyData = await superAdminStatsService.getN3MonthlyByWorkCenter(this.selectedYear);
      } catch (e) { console.error('n3Monthly:', e); } finally { this.loadingN3Monthly = false; }
    },
    async loadMonthlyClockEntries() {
      this.loadingMonthly = true;
      try {
        this.monthlyClockData = await superAdminStatsService.getClockEntriesMonthly(this.selectedYear);
      } catch (e) { console.error('monthlyClockEntries:', e); } finally { this.loadingMonthly = false; }
    },

    formatKpiValue(kpi) {
      if (kpi.value === undefined || kpi.value === null) return '—';
      if (kpi.isCurrency) return `${parseFloat(kpi.value).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`;
      return kpi.value.toLocaleString('es-ES');
    },

    workerBarWidth(count) {
      const max = Math.max(...this.workersData.map((r) => r.workers), 1);
      return Math.round((count / max) * 100);
    },

    shortName(name) {
      if (!name) return '';
      return name.length > 20 ? `${name.substring(0, 18)}…` : name;
    },
  },
};
</script>

<style lang="scss" scoped>
// ════════════════════════════════════════
//  TOKENS & PAGE LAYOUT
// ════════════════════════════════════════
.superadmin-stats {
  padding: calc(var(--base) * 2) calc(var(--base) * 2);
  display: flex;
  flex-direction: column;
  gap: calc(var(--base) * 2.5);

  @media (min-width: 1024px) {
    padding: calc(var(--base) * 3) calc(var(--base) * 3);
  }
}

.sa-header-flex {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.sa-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

// ════════════════════════════════════════
//  PAGE HEADER (Aligned with Fichajes.vue)
// ════════════════════════════════════════
.sa-page-header {
  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, var(--v-primary-base, #1976D2), #424242);
    color: #fff;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 1.2px;
    padding: 3px 10px;
    border-radius: 20px;
    text-transform: uppercase;
  }
}

.sa-year-picker {
  min-width: 130px;
  max-width: 160px;
}

.sa-refresh-btn {
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}

// ════════════════════════════════════════
//  KPI CARDS
// ════════════════════════════════════════
.sa-kpi-row {
  gap: 0;
  margin: 0 -8px;
  display: flex;
  flex-wrap: wrap;

  >.v-col {
    padding: 8px;
  }
}

.sa-kpi-card {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--sa-card-bg, rgba(255, 255, 255, 0.06));
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  .theme--light & {
    --sa-card-bg: #ffffff;
    border-color: rgba(0, 0, 0, 0.07);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }

  .theme--dark & {
    --sa-card-bg: rgba(255, 255, 255, 0.05);
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.3);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14) !important;
  }

  &__icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__body {
    flex-grow: 1;
    min-width: 0;
  }

  &__value {
    font-size: 1.4rem;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__label {
    font-size: 0.72rem;
    font-weight: 500;
    opacity: 0.6;
    margin-top: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__glow {
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    background: linear-gradient(135deg, var(--sa-glow-color, transparent) 0%, transparent 60%);
    opacity: 0.08;
  }

  &--primary {
    .sa-kpi-card__glow {
      --sa-glow-color: var(--v-primary-base, #1976D2);
    }
  }

  &--info {
    .sa-kpi-card__glow {
      --sa-glow-color: #00BCD4;
    }
  }

  &--success {
    .sa-kpi-card__glow {
      --sa-glow-color: var(--v-success-base, #4CAF50);
    }
  }

  &--danger {
    .sa-kpi-card__glow {
      --sa-glow-color: var(--v-error-base, #EF5350);
    }
  }

  &--gold {
    .sa-kpi-card__glow {
      --sa-glow-color: #FFC107;
    }
  }

  &--secondary {
    .sa-kpi-card__glow {
      --sa-glow-color: var(--v-secondary-base, #424242);
    }
  }
}

// ════════════════════════════════════════
//  TABS
// ════════════════════════════════════════
.sa-content-card {
  margin-top: 0 !important;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.sa-tab-pane {
  padding: 12px 12px 24px 12px;

  @media (min-width: 768px) {
    padding: 16px 20px 32px 20px;
  }
}

.sa-tab-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 4px;
}

// ════════════════════════════════════════
//  PANEL (glass card)
// ════════════════════════════════════════
.sa-panel {
  border-radius: 16px;
  overflow: hidden;
  transition: box-shadow 0.2s;

  .theme--light & {
    background: #fff;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }

  .theme--dark & {
    background: rgba(255, 255, 255, 0.04);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  &:hover {
    box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12) !important;
  }

  &__header {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    font-weight: 600;
    font-size: 0.875rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    .theme--dark & {
      border-bottom-color: rgba(255, 255, 255, 0.07);
    }
  }

  &__body {
    padding: 16px;
    position: relative;
  }
}

// ════════════════════════════════════════
//  CHART WRAPPER
// ════════════════════════════════════════
.sa-chart-wrap {
  height: 300px;
  position: relative;
}

.sa-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-surface, 255, 255, 255), 0.7);
  z-index: 2;
  border-radius: inherit;
}

// ════════════════════════════════════════
//  FILTER BAR
// ════════════════════════════════════════
.sa-filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-radius: 12px;

  .theme--light & {
    background: #f5f7fa;
  }

  .theme--dark & {
    background: rgba(255, 255, 255, 0.04);
  }

  &__title {
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    font-weight: 600;
    opacity: 0.7;
    white-space: nowrap;
  }
}

.sa-date-field {
  max-width: 180px;
}

.sa-quick-range {
  margin: 0 !important;

  .v-chip {
    cursor: pointer;
    font-size: 0.75rem;
  }
}

// ════════════════════════════════════════
//  RANKING
// ════════════════════════════════════════
.sa-ranking-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);

  .theme--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }

  &--top {
    font-weight: 600;
  }

  &__pos {
    width: 28px;
    text-align: center;

    .rank-num {
      font-size: 0.8rem;
      opacity: 0.5;
    }
  }

  &__name {
    flex: 1;
    font-size: 0.82rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__bar-wrap {
    width: 80px;
    height: 6px;
    background: rgba(0, 0, 0, 0.08);
    border-radius: 3px;
    overflow: hidden;
    flex-shrink: 0;

    .theme--dark & {
      background: rgba(255, 255, 255, 0.1);
    }
  }

  &__bar {
    height: 100%;
    background: var(--v-primary-base, #1976D2);
    border-radius: 3px;
    transition: width 0.6s ease;
  }

  &__count {
    font-weight: 700;
    font-size: 0.9rem;
    min-width: 30px;
    text-align: right;
  }
}

// ════════════════════════════════════════
//  STAT MINI
// ════════════════════════════════════════
.sa-stat-mini {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  height: 100%;

  .theme--light & {
    background: #f7f9fc;
    border: 1px solid rgba(0, 0, 0, 0.06);
  }

  .theme--dark & {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.06);
  }

  &--danger {
    .theme--light & {
      background: #fff5f5;
      border-color: rgba(239, 83, 80, 0.2);
    }

    .theme--dark & {
      background: rgba(239, 83, 80, 0.08);
      border-color: rgba(239, 83, 80, 0.2);
    }
  }

  &--success {
    .theme--light & {
      background: #f5fff8;
      border-color: rgba(102, 187, 106, 0.2);
    }

    .theme--dark & {
      background: rgba(102, 187, 106, 0.08);
      border-color: rgba(102, 187, 106, 0.2);
    }
  }

  &--gold {
    .theme--light & {
      background: #fffdf0;
      border-color: rgba(255, 193, 7, 0.3);
    }

    .theme--dark & {
      background: rgba(255, 193, 7, 0.08);
      border-color: rgba(255, 193, 7, 0.2);
    }
  }

  &__value {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1;
  }

  &__label {
    font-size: 0.75rem;
    opacity: 0.6;
    margin-top: 4px;
  }
}

// ════════════════════════════════════════
//  CRITICAL ROW
// ════════════════════════════════════════
.sa-critical-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);

  .theme--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }

  &__name {
    font-size: 0.82rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    padding-right: 8px;
  }
}

// ════════════════════════════════════════
//  MINI TABLE
// ════════════════════════════════════════
.sa-mini-table {
  font-size: 0.82rem;
}

// ════════════════════════════════════════
//  LEGEND
// ════════════════════════════════════════
.sa-legend-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
}

.sa-legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;

  &--error {
    background: #EF5350;
  }

  &--success {
    background: #66BB6A;
  }
}

// ════════════════════════════════════════
//  EMPTY & SKELETON
// ════════════════════════════════════════
.sa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;

  p {
    margin-top: 12px;
    opacity: 0.6;
    font-size: 0.85rem;
  }
}

.sa-skeleton {
  display: inline-block;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.08) 25%, rgba(0, 0, 0, 0.04) 50%, rgba(0, 0, 0, 0.08) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite;
  border-radius: 4px;

  &--sm {
    width: 60px;
    height: 1.4rem;
  }

  .theme--dark & {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.1) 25%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.1) 75%);
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}
</style>
